import axios from 'axios';
import type { StripeConnectInvite } from './api.settings';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const CONNECT_BASE = `${API_BASE}/settings/stripe-connect`;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token') || ''}` };
}

/** A manager may connect more than one Stripe account, so every call here is scoped to one
 * specific "Stripe"-type PaymentMethod id. Live status for each is embedded directly on the
 * PaymentMethod returned by listPaymentMethods() — there's no separate status endpoint. */

/** Returns a Stripe-hosted onboarding URL to redirect the manager to. */
export async function startStripeConnect(paymentMethodId: number): Promise<{ url: string }> {
  const res = await axios.post(`${CONNECT_BASE}/${paymentMethodId}/start`, {}, { headers: authHeaders() });
  return res.data;
}

/** Abandons a stuck/incomplete onboarding so the manager can start over with a fresh account.
 * Server refuses (409) once the account is already active. */
export async function resetStripeConnect(paymentMethodId: number): Promise<void> {
  await axios.post(`${CONNECT_BASE}/${paymentMethodId}/reset`, {}, { headers: authHeaders() });
}

/** Invites the apartment's owner to complete onboarding in the manager's place, so Stripe asks
 * them for their own identity documents and bank account. Any invitation already outstanding for
 * this payment method is revoked server-side. Returns the invitation including its URL — worth
 * showing even when mail is enabled, and the only route when it isn't. */
export async function inviteStripeOwner(
  paymentMethodId: number, email: string,
): Promise<StripeConnectInvite> {
  const res = await axios.post(`${CONNECT_BASE}/${paymentMethodId}/invite`, { email }, { headers: authHeaders() });
  return res.data;
}

/** Cancels the outstanding invitation. Deliberately leaves any part-created Stripe account alone —
 * resetStripeConnect is the tool for that. */
export async function revokeStripeOwnerInvite(paymentMethodId: number): Promise<void> {
  await axios.delete(`${CONNECT_BASE}/${paymentMethodId}/invite`, { headers: authHeaders() });
}
